import React, { useState } from "react";
import { Modal, Radio, Space, Button, Typography } from "antd";
import {
  MailOutlined,
  MobileOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { OtpVerificationMethodEnum } from "../../shared/constants/constants";

const { Title, Text } = Typography;

/**
 * TwoFactorMethodSelector Component
 * Allows users to select their preferred 2FA verification method before login
 * 
 * @param {boolean} visible - Controls modal visibility
 * @param {function} onSelect - Callback when user selects a method
 * @param {function} onCancel - Callback when user cancels
 * @param {boolean} loading - Shows loading state on confirm button
 */
const TwoFactorMethodSelector = ({
  visible,
  onSelect,
  onCancel,
  loading = false,
}) => {
  const [selectedMethod, setSelectedMethod] = useState(
    OtpVerificationMethodEnum.EMAIL.value
  );

  const getIcon = (method) => {
    switch (method) {
      case OtpVerificationMethodEnum.EMAIL.value:
        return <MailOutlined style={{ fontSize: 24 }} />;
      case OtpVerificationMethodEnum.SMS.value:
        return <MobileOutlined style={{ fontSize: 24 }} />;
      case OtpVerificationMethodEnum.AUTHENTICATOR_APP.value:
        return <SafetyOutlined style={{ fontSize: 24 }} />;
      default:
        return <MailOutlined style={{ fontSize: 24 }} />;
    }
  };

  const handleConfirm = () => {
    onSelect(selectedMethod);
  };

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Select Verification Method
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Choose how you would like to receive your verification code
          </Text>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          loading={loading}
        >
          Continue
        </Button>,
      ]}
      width={500}
      centered
    >
      <div style={{ padding: "20px 0" }}>
        <Radio.Group
          value={selectedMethod}
          onChange={(e) => setSelectedMethod(e.target.value)}
          style={{ width: "100%" }}
        >
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Radio
              value={OtpVerificationMethodEnum.EMAIL.value}
              style={{
                width: "100%",
                padding: "16px",
                border: "1px solid #d9d9d9",
                borderRadius: "8px",
                backgroundColor:
                  selectedMethod === OtpVerificationMethodEnum.EMAIL.value
                    ? "#f0f5ff"
                    : "transparent",
              }}
            >
              <Space>
                {getIcon(OtpVerificationMethodEnum.EMAIL.value)}
                <div>
                  <div style={{ fontWeight: 500 }}>
                    {OtpVerificationMethodEnum.EMAIL.description}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Receive verification code via email
                  </Text>
                </div>
              </Space>
            </Radio>

            <Radio
              value={OtpVerificationMethodEnum.SMS.value}
              style={{
                width: "100%",
                padding: "16px",
                border: "1px solid #d9d9d9",
                borderRadius: "8px",
                backgroundColor:
                  selectedMethod === OtpVerificationMethodEnum.SMS.value
                    ? "#f0f5ff"
                    : "transparent",
              }}
            >
              <Space>
                {getIcon(OtpVerificationMethodEnum.SMS.value)}
                <div>
                  <div style={{ fontWeight: 500 }}>
                    {OtpVerificationMethodEnum.SMS.description}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Receive verification code via SMS
                  </Text>
                </div>
              </Space>
            </Radio>

            <Radio
              value={OtpVerificationMethodEnum.AUTHENTICATOR_APP.value}
              style={{
                width: "100%",
                padding: "16px",
                border: "1px solid #d9d9d9",
                borderRadius: "8px",
                backgroundColor:
                  selectedMethod ===
                  OtpVerificationMethodEnum.AUTHENTICATOR_APP.value
                    ? "#f0f5ff"
                    : "transparent",
              }}
            >
              <Space>
                {getIcon(OtpVerificationMethodEnum.AUTHENTICATOR_APP.value)}
                <div>
                  <div style={{ fontWeight: 500 }}>
                    {OtpVerificationMethodEnum.AUTHENTICATOR_APP.description}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Use your authenticator app (Google Authenticator, Authy,
                    etc.)
                  </Text>
                </div>
              </Space>
            </Radio>
          </Space>
        </Radio.Group>
      </div>
    </Modal>
  );
};

export default TwoFactorMethodSelector;
